import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Typography,
    Box,
    Chip,
    Stack,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tooltip,
    Divider,
    Paper,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Star,
    ThumbUp,
    Favorite,
    EmojiEvents,
    Celebration,
    Comment,
    ExpandMore,
    LocalShipping,
    Security,
    Route,
    EmojiPeople,
    Timeline,
    Send,
    WorkspacePremium
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';

const REACTION_ICONS = {
    like: { icon: ThumbUp, label: 'Like', color: '#1976d2' },
    love: { icon: Favorite, label: 'Love', color: '#d32f2f' },
    wow: { icon: EmojiEvents, label: 'Wow', color: '#ed6c02' },
    congrats: { icon: Celebration, label: 'Congrats', color: '#2e7d32' }
};

// Removed unused SAFETY_RATING_COLORS to satisfy linter

function DriverSpotlight() {
    const [spotlight, setSpotlight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reactionLoading, setReactionLoading] = useState(false);
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const { user } = useAuth();

    const fetchSpotlight = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/team-engagement/spotlight/current`, {
                params: { ts: Date.now() }
            });
            // Handle 204 No Content or missing payload gracefully
            if (response.status === 204 || !response.data || !response.data.data) {
                setSpotlight(null);
                setError('No driver spotlight currently featured');
            } else {
                setSpotlight(response.data.data);
                setError(null);
            }
        } catch (err) {
            // 404 is expected when no spotlight is active - handle silently
            if (err.response?.status === 404) {
                setSpotlight(null);
                setError('No driver spotlight currently featured');
            } else {
                // Only log unexpected errors
                console.error('Error fetching driver spotlight:', err);
                setError('Failed to load driver spotlight');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSpotlight();
        // Refetch periodically and on tab focus to avoid stale data
        const intervalId = setInterval(fetchSpotlight, 60000); // 60s
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchSpotlight();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    const handleReaction = async (type) => {
        if (!spotlight || reactionLoading) return;
        
        try {
            setReactionLoading(true);
            await apiClient.post(`/team-engagement/spotlight/${spotlight._id}/reaction`, { type });
            await fetchSpotlight(); // Refresh to get updated reaction counts
        } catch (err) {
            console.error('Error adding reaction:', err);
        } finally {
            setReactionLoading(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim() || commentLoading) return;
        
        try {
            setCommentLoading(true);
            await apiClient.post(`/team-engagement/spotlight/${spotlight._id}/comment`, {
                message: newComment.trim()
            });
            setNewComment('');
            setCommentDialogOpen(false);
            await fetchSpotlight(); // Refresh to get updated comments
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setCommentLoading(false);
        }
    };

    if (loading) {
        return <LoadingState title="Loading driver spotlight..." />;
    }

    if (error) {
        return (
            <EmptyState
                icon={EmojiPeople}
                title={error}
                subtitle="Check back soon for our next featured driver!"
                actionLabel={user && (user.role === 'admin' || user.role === 'company_admin') ? 'Manage Driver Spotlight' : undefined}
                onAction={user && (user.role === 'admin' || user.role === 'company_admin') ? () => { try { window.location.assign('/admin?tab=spotlight'); } catch (_) {} } : undefined}
            />
        );
    }

    const driver = spotlight.driverId;
    const bio = spotlight.spotlight;
    const reactionCounts = spotlight.reactionCounts || {};
    const totalReactions = Object.values(reactionCounts).reduce((sum, count) => sum + count, 0);

    return (
        <>
            <Card sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decoration */}
                <Box sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    zIndex: 0
                }} />
                
                <CardHeader
                    avatar={
                        <Avatar
                            src={spotlight.profileImageUrl}
                            sx={{ 
                                width: 48, 
                                height: 48, 
                                border: '3px solid rgba(255,255,255,0.2)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }}
                        >
                            {driver?.name?.charAt(0) || 'D'}
                        </Avatar>
                    }
                    title={
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
                                {spotlight.title}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0 }}>
                                {driver?.name || 'Featured Driver'}
                            </Typography>
                        </Box>
                    }
                    action={
                        <Chip
                            icon={<Star />}
                            label="Featured"
                            size="small"
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.9)', 
                                color: '#667eea',
                                fontWeight: 'bold'
                            }}
                        />
                    }
                    sx={{ position: 'relative', zIndex: 1, pb: 0, pt: 1 }}
                />
                
                <CardContent sx={{ position: 'relative', zIndex: 1, pt: 1, pb: 1 }}>
                    {/* Bio */}
                    <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', opacity: 0.95 }}>
                        "{bio.bio}"
                    </Typography>
                    
                    {/* Quick Stats */}
                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                        {bio.routeSpecialty && (
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Route sx={{ color: 'white', fontSize: 16 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>Specialty</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                {bio.routeSpecialty}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        )}
                        
                        {bio.yearsOfExperience && (
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Timeline sx={{ color: 'white', fontSize: 16 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>Experience</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                {bio.yearsOfExperience} years
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        )}
                        
                        {bio.safetyRecord?.daysWithoutIncident && (
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Security sx={{ color: 'white', fontSize: 16 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>Safety Record</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                {bio.safetyRecord.daysWithoutIncident} days
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        )}
                        
                        {bio.milesThisYear && (
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <LocalShipping sx={{ color: 'white', fontSize: 16 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>Miles This Year</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                {bio.milesThisYear.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                    
                    {/* Fun Fact */}
                    {bio.funFact && (
                        <Paper sx={{ 
                            p: 1, 
                            mb: 1, 
                            bgcolor: 'rgba(255,255,255,0.1)', 
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                                💡 Fun Fact
                            </Typography>
                            <Typography variant="caption">{bio.funFact}</Typography>
                        </Paper>
                    )}
                    
                    {/* Q&A Section */}
                    {spotlight.qAndA && spotlight.qAndA.length > 0 && (
                        <Accordion 
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                backdropFilter: 'blur(10px)',
                                '&:before': { display: 'none' },
                                borderRadius: 1,
                                mb: 1
                            }}
                        >
                            <AccordionSummary 
                                expandIcon={<ExpandMore sx={{ color: 'white' }} />}
                                sx={{ minHeight: 'auto', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>💬 Q&A with {driver?.name}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                {spotlight.qAndA.map((qa, index) => (
                                    <Box key={index} sx={{ mb: index < spotlight.qAndA.length - 1 ? 1 : 0 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.25, display: 'block' }}>
                                            Q: {qa.question}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                                            A: {qa.answer}
                                        </Typography>
                                        {index < spotlight.qAndA.length - 1 && <Divider sx={{ mt: 0.75, bgcolor: 'rgba(255,255,255,0.2)' }} />}
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    )}
                    
                    {/* Reactions */}
                    <Box sx={{ mt: 1.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                            {Object.entries(REACTION_ICONS).map(([type, config]) => {
                                const IconComponent = config.icon;
                                const count = reactionCounts[type] || 0;
                                const isUserReaction = spotlight.userReaction === type;
                                
                                return (
                                    <Tooltip key={type} title={`${config.label} (${count})`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleReaction(type)}
                                            disabled={reactionLoading}
                                            sx={{
                                                color: isUserReaction ? config.color : 'rgba(255,255,255,0.7)',
                                                bgcolor: isUserReaction ? 'rgba(255,255,255,0.2)' : 'transparent',
                                                '&:hover': { 
                                                    bgcolor: 'rgba(255,255,255,0.1)',
                                                    color: config.color
                                                },
                                                minWidth: 'auto',
                                                px: count > 0 ? 0.75 : 0.5,
                                                py: 0.25
                                            }}
                                        >
                                            <IconComponent fontSize="small" />
                                            {count > 0 && (
                                                <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                    {count}
                                                </Typography>
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                );
                            })}
                            
                            <Button
                                startIcon={<Comment />}
                                onClick={() => setCommentDialogOpen(true)}
                                size="small"
                                sx={{ 
                                    color: 'white', 
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    py: 0.25
                                }}
                                variant="outlined"
                            >
                                Comment ({spotlight.comments?.length || 0})
                            </Button>
                        </Stack>
                        
                        {totalReactions > 0 && (
                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                {totalReactions} {totalReactions === 1 ? 'reaction' : 'reactions'}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
            
            {/* Comment Dialog */}
            <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>💬 Leave a comment for {driver?.name}</DialogTitle>
                <DialogContent>
                    {spotlight.comments && spotlight.comments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent comments:</Typography>
                            <List dense>
                                {spotlight.comments.slice(-3).map((comment, index) => (
                                    <ListItem key={index} alignItems="flex-start">
                                        <ListItemAvatar>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                {comment.userId?.name?.charAt(0) || 'U'}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={comment.userId?.name || 'User'}
                                            secondary={comment.message}
                                            secondaryTypographyProps={{ sx: { whiteSpace: 'pre-wrap' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                            <Divider sx={{ my: 2 }} />
                        </Box>
                    )}
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Share your thoughts, congratulations, or encouragement..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${newComment.length}/500 characters`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || commentLoading}
                        variant="contained"
                        startIcon={commentLoading ? null : <Send />}
                    >
                        {commentLoading ? 'Posting...' : 'Post Comment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default DriverSpotlight; 